import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight, X, Ban, Edit3, ClipboardCheck } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { groupColor, STATUS_ICON } from "@/utils/groupColors";
import { FastAttendanceModal } from "@/components/attendance/FastAttendanceModal";
import { formatSessionDate, formatSessionTime, tzWeekdayMonFirst, localDateTimeToUtcIso, tzToday, addDaysStr } from "@/utils/datetime";

type Session = {
    id: string; group_id: string;
    start_at: string; end_at: string;
    scheduled_date: string; start_time: string; end_time: string;
    teacher_person_id: string | null; room_id: string | null;
    status: "scheduled" | "held" | "cancelled" | "moved";
    cancel_reason: string | null; attendance_closed: boolean;
    tenant_timezone: string;
};
type Group = { id: string; name: string; branch_id: string };
type Room = { id: string; name: string };
type Person = { id: string; first_name: string; last_name: string };
type Branch = { id: string; name: string };
type Rule = { id: string; group_id: string; weekday: number; start_time: string; end_time: string; teacher_person_id: string | null; room_id: string | null; effective_from: string };
type View = "day" | "week" | "month";

function parseDateStr(s: string): Date { const [Y, M, D] = s.split("-").map(Number); return new Date(Date.UTC(Y, M - 1, D)); }
function fmtDateStr(d: Date): string { return d.toISOString().slice(0, 10); }
function shiftDate(s: string, n: number): string { return addDaysStr(s, n); }
function startOfWeekStr(s: string): string {
    const d = parseDateStr(s);
    const dow = (d.getUTCDay() + 6) % 7; // 0=Mon
    return addDaysStr(s, -dow);
}
function startOfMonthStr(s: string): string { const [Y, M] = s.split("-").map(Number); return `${Y}-${String(M).padStart(2, "0")}-01`; }
function endOfMonthStr(s: string): string {
    const [Y, M] = s.split("-").map(Number);
    const d = new Date(Date.UTC(Y, M, 0));
    return d.toISOString().slice(0, 10);
}

export default function Schedule() {
    const { t } = useTranslation();
    const { hasPerm, activeMembership } = useAuth();
    const canView = hasPerm("schedule.view");
    const tz = activeMembership?.tenant.timezone || "Asia/Baku";
    const [view, setView] = useState<View>("week");
    const [anchor, setAnchor] = useState<string>(tzToday(tz));
    const [sessions, setSessions] = useState<Session[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    // Stable order (by id) so every group keeps its own distinct colour across reloads.
    const groupOrder = useMemo(() => groups.map((g) => g.id).sort(), [groups]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [people, setPeople] = useState<Person[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [fBranch, setFBranch] = useState("");
    const [fGroup, setFGroup] = useState("");
    const [fTeacher, setFTeacher] = useState("");
    const [fRoom, setFRoom] = useState("");
    const [selected, setSelected] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const range = useMemo(() => {
        if (view === "day") return { from: anchor, to: anchor };
        if (view === "week") { const s = startOfWeekStr(anchor); return { from: s, to: addDaysStr(s, 6) }; }
        return { from: startOfMonthStr(anchor), to: endOfMonthStr(anchor) };
    }, [view, anchor]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const params = new URLSearchParams();
            params.set("date_from", range.from);
            params.set("date_to", range.to);
            if (fGroup) params.set("group_id", fGroup);
            if (fTeacher) params.set("teacher_person_id", fTeacher);
            if (fRoom) params.set("room_id", fRoom);
            const [s, g, r, p, b] = await Promise.all([
                api.get(`/sessions?${params}`),
                api.get(`/groups`),
                api.get(`/rooms`),
                api.get(`/people?limit=200`),
                api.get(`/branches`),
            ]);
            let sess = s.data.items as Session[];
            if (fBranch) {
                const groupIdsForBranch = new Set((g.data.items as Group[]).filter(gr => gr.branch_id === fBranch).map(gr => gr.id));
                sess = sess.filter(x => groupIdsForBranch.has(x.group_id));
            }
            setSessions(sess);
            setGroups(g.data.items);
            setRooms(r.data.items);
            setPeople(p.data.items);
            setBranches(b.data.items);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    }, [range.from, range.to, fGroup, fTeacher, fRoom, fBranch]);
    useEffect(() => { load(); }, [load]);

    if (!canView) return <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text)" }} data-testid="schedule-perm-denied">{t("common.no_permission")}</div>;

    const shift = (dir: -1 | 1) => setAnchor((a) => shiftDate(a, view === "day" ? dir : view === "week" ? 7 * dir : 30 * dir));

    const daysInView: string[] = [];
    if (view === "day") daysInView.push(range.from);
    else if (view === "week") for (let i = 0; i < 7; i++) daysInView.push(addDaysStr(range.from, i));
    else { let c = range.from; while (c <= range.to) { daysInView.push(c); c = addDaysStr(c, 1); } }

    // Group sessions by their TENANT-LOCAL calendar date derived from start_at + tz.
    const byDay: Record<string, Session[]> = {};
    for (const s of sessions) {
        const key = formatSessionDate(s.start_at, tz);
        (byDay[key] ||= []).push(s);
    }
    for (const k of Object.keys(byDay)) byDay[k].sort((a, b) => a.start_at.localeCompare(b.start_at));

    return (
        <div className="space-y-4" data-testid="schedule-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("nav.schedule")}</h1>
                <div className="flex gap-2 items-center flex-wrap">
                    <button data-testid="prev-btn" onClick={() => shift(-1)} className="rounded-md p-2 border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 40, minWidth: 40 }}><ChevronLeft size={16}/></button>
                    <button data-testid="today-btn" onClick={() => setAnchor(tzToday(tz))} className="rounded-md px-3 py-2 text-xs border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 40 }}>{t("schedule.today")}</button>
                    <button data-testid="next-btn" onClick={() => shift(1)} className="rounded-md p-2 border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 40, minWidth: 40 }}><ChevronRight size={16}/></button>
                    <span className="text-sm mx-2 tabular-nums" style={{ color: "var(--text)" }} data-testid="range-label">{range.from} — {range.to}</span>
                    <div className="flex rounded-md border overflow-hidden" style={{ borderColor: "var(--border)" }}>
                        {(["day", "week", "month"] as View[]).map((v) => (
                            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className="px-3 py-2 text-xs" style={{ background: view === v ? "var(--primary)" : "var(--input)", color: view === v ? "var(--primary-fg)" : "var(--text-muted)", minHeight: 40 }}>{t(`schedule.view.${v}`)}</button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="surface-card p-3 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="schedule-filters">
                <select data-testid="filter-branch" value={fBranch} onChange={(e) => setFBranch(e.target.value)} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                    <option value="">{t("schedule.filter.all_branches")}</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select data-testid="filter-group" value={fGroup} onChange={(e) => setFGroup(e.target.value)} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                    <option value="">{t("schedule.filter.all_groups")}</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                <select data-testid="filter-teacher" value={fTeacher} onChange={(e) => setFTeacher(e.target.value)} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                    <option value="">{t("schedule.filter.all_teachers")}</option>
                    {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
                <select data-testid="filter-room" value={fRoom} onChange={(e) => setFRoom(e.target.value)} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                    <option value="">{t("schedule.filter.all_rooms")}</option>
                    {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {loading ? <div className="p-12 flex justify-center"><Loader2 className="animate-spin"/></div> : (
                <div className={view === "month" ? "grid grid-cols-7 gap-2" : view === "week" ? "grid grid-cols-1 md:grid-cols-7 gap-2" : "grid grid-cols-1 gap-2"} data-testid="schedule-grid">
                    {daysInView.map((key) => {
                        const rows = byDay[key] || [];
                        const d = parseDateStr(key);
                        const wd = (d.getUTCDay() + 6) % 7;
                        return (
                            <div key={key} className="surface-card p-2 min-h-24" data-testid={`day-${key}`}>
                                <div className="text-xs mb-2 flex items-center justify-between">
                                    <span style={{ color: "var(--text-muted)" }}>{t(`weekday.${wd}` as any)}</span>
                                    <span className="font-mono tabular-nums" style={{ color: "var(--text)" }}>{d.getUTCDate()}</span>
                                </div>
                                {rows.length === 0 ? <div className="text-xs" style={{ color: "var(--text-muted)" }}>—</div> : rows.map((s) => {
                                    const g = groups.find(x => x.id === s.group_id);
                                    const c = groupColor(s.group_id || g?.name || "", groupOrder.indexOf(s.group_id));
                                    const st = STATUS_ICON[s.status as keyof typeof STATUS_ICON] || STATUS_ICON.scheduled;
                                    const cancelled = s.status === "cancelled";
                                    return (
                                        <button key={s.id} data-testid={`session-${s.id}`} data-group-color={c.dot} onClick={() => setSelected(s)}
                                            className="relative w-full text-left rounded-md pl-2.5 pr-2 py-1.5 mb-1 text-xs border transition-[transform,box-shadow] duration-150 hover:-translate-y-px hover:shadow-md"
                                            style={{ minHeight: 32, background: c.bg, borderColor: c.border, borderLeft: `3px solid ${c.dot}`, color: "var(--text)", opacity: cancelled ? 0.6 : 1 }}>
                                            <div className="flex items-center justify-between gap-1">
                                                <span className={`font-mono ${cancelled ? "line-through" : ""}`}>{formatSessionTime(s.start_at, tz)}–{formatSessionTime(s.end_at, tz)}</span>
                                                <span title={t(`session.status.${s.status}` as any, { defaultValue: s.status })} data-testid={`session-status-${s.id}`} style={{ color: st.color }}><st.Icon size={12} /></span>
                                            </div>
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: c.dot }} />
                                                <span className="truncate font-medium">{g?.name || "—"}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
            {selected && <SessionDrawer session={selected} tz={tz} groups={groups} rooms={rooms} people={people} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }}/>}
        </div>
    );
}

function SessionDrawer({ session, tz, groups, rooms, people, onClose, onChanged }: { session: Session; tz: string; groups: Group[]; rooms: Room[]; people: Person[]; onClose: () => void; onChanged: () => void }) {
    const { t } = useTranslation();
    const nav = useNavigate();
    const { hasPerm } = useAuth();
    const canManage = hasPerm("session.manage");
    const [fast, setFast] = useState(false);
    const [rule, setRule] = useState<Rule | null>(null);
    const localDate = formatSessionDate(session.start_at, tz);
    const localStart = formatSessionTime(session.start_at, tz);
    const localEnd = formatSessionTime(session.end_at, tz);
    const [editing, setEditing] = useState<null | { scheduled_date: string; start_time: string; end_time: string; room_id: string; teacher_person_id: string }>(null);
    const [scopeChoice, setScopeChoice] = useState<"one" | "future">("one");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/schedule/rules?group_id=${session.group_id}`);
                const wd = tzWeekdayMonFirst(session.start_at, tz);
                const found = (r.data.items as Rule[]).find(x => x.weekday === wd && x.start_time === localStart);
                setRule(found || null);
            } catch { /* rule optional */ }
        })();
    }, [session, tz, localStart]);

    const g = groups.find(x => x.id === session.group_id);
    const room = session.room_id ? rooms.find(x => x.id === session.room_id) : null;
    const teacher = session.teacher_person_id ? people.find(x => x.id === session.teacher_person_id) : null;

    const openEdit = () => setEditing({
        scheduled_date: localDate, start_time: localStart, end_time: localEnd,
        room_id: session.room_id || "", teacher_person_id: session.teacher_person_id || "",
    });

    const doCancel = async () => {
        const reason = prompt(t("schedule.cancel_prompt") as string);
        if (!reason) return;
        setBusy(true);
        try { await api.post(`/sessions/${session.id}/cancel`, { reason }); onChanged(); }
        catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    const submitEdit = async () => {
        if (!editing) return;
        setBusy(true); setErr(null);
        try {
            if (scopeChoice === "one" || !rule) {
                await api.patch(`/sessions/${session.id}`, {
                    scheduled_date: editing.scheduled_date,
                    start_time: editing.start_time,
                    end_time: editing.end_time,
                    teacher_person_id: editing.teacher_person_id || null,
                    room_id: editing.room_id || null,
                });
            } else {
                const until = addDaysStr(editing.scheduled_date, 180);
                await api.post(`/schedule/rules/${rule.id}/split`, {
                    from_date: editing.scheduled_date,
                    start_time: editing.start_time,
                    end_time: editing.end_time,
                    teacher_person_id: editing.teacher_person_id || null,
                    room_id: editing.room_id || null,
                    until,
                });
            }
            onChanged();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="session-drawer">
            {fast && <FastAttendanceModal sessionId={session.id} onClose={() => setFast(false)} onSaved={() => { setFast(false); onChanged(); }} />}
            <div className="absolute right-0 top-0 bottom-0 w-full sm:w-96 surface-card overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{g?.name || "—"}</h2>
                    <button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }}/></button>
                </div>
                <dl className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("session.date")}</dt><dd style={{ color: "var(--text)" }}>{localDate}</dd></div>
                    <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("session.time")}</dt><dd className="font-mono" style={{ color: "var(--text)" }}>{localStart}–{localEnd}</dd></div>
                    <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("common.status")}</dt><dd><span className={`status-pill ${session.status === "cancelled" ? "status-error" : session.status === "held" ? "status-success" : session.status === "moved" ? "status-warning" : "status-info"}`}>{t(`session.status.${session.status}` as any)}</span></dd></div>
                    <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("groups.room")}</dt><dd style={{ color: "var(--text)" }}>{room?.name || "—"}</dd></div>
                    <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("nav.teachers")}</dt><dd style={{ color: "var(--text)" }}>{teacher ? `${teacher.first_name} ${teacher.last_name}` : "—"}</dd></div>
                    <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>TZ</dt><dd className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{tz}</dd></div>
                    {session.cancel_reason && <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("schedule.cancel_reason")}</dt><dd style={{ color: "var(--text)" }}>{session.cancel_reason}</dd></div>}
                </dl>
                {err && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="drawer-error">{err}</div>}
                {!editing && (
                    <div className="space-y-2">
                        <button data-testid="attendance-link" onClick={() => (hasPerm("attendance.mark") && session.status !== "cancelled" ? setFast(true) : nav(`/groups/${session.group_id}`))} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium" style={{ background: "var(--success-bg)", color: "var(--success-fg)", minHeight: 48 }}><ClipboardCheck size={16}/> {t("fast_att.quick_btn")}</button>
                        {canManage && session.status !== "cancelled" && (
                            <>
                                <button data-testid="edit-session-btn" onClick={openEdit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}><Edit3 size={14}/> {t("schedule.reschedule")}</button>
                                <button data-testid="cancel-session-btn" disabled={busy} onClick={doCancel} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--error-fg)", minHeight: 44 }}><Ban size={14}/> {t("schedule.cancel")}</button>
                            </>
                        )}
                    </div>
                )}
                {editing && (
                    <div className="space-y-3">
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("session.date")}</div><input data-testid="edit-date" type="date" value={editing.scheduled_date} onChange={(e) => setEditing({ ...editing, scheduled_date: e.target.value })} className="surface-input w-full px-2 py-2 text-sm" style={{ minHeight: 40 }}/></label>
                        <div className="grid grid-cols-2 gap-2">
                            <label><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("session.time")} —</div><input data-testid="edit-start" type="time" value={editing.start_time} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} className="surface-input w-full px-2 py-2 text-sm" style={{ minHeight: 40 }}/></label>
                            <label><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("session.time")} +</div><input data-testid="edit-end" type="time" value={editing.end_time} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} className="surface-input w-full px-2 py-2 text-sm" style={{ minHeight: 40 }}/></label>
                        </div>
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.room")}</div><select data-testid="edit-room" value={editing.room_id} onChange={(e) => setEditing({ ...editing, room_id: e.target.value })} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}><option value="">—</option>{rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></label>
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("nav.teachers")}</div><select data-testid="edit-teacher" value={editing.teacher_person_id} onChange={(e) => setEditing({ ...editing, teacher_person_id: e.target.value })} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}><option value="">—</option>{people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}</select></label>

                        {rule && (
                            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--border)" }} data-testid="scope-choice">
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("schedule.scope.title")}</div>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input data-testid="scope-one" type="radio" checked={scopeChoice === "one"} onChange={() => setScopeChoice("one")}/>
                                    <div className="text-sm">
                                        <div style={{ color: "var(--text)" }}>{t("schedule.scope.one")}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("schedule.scope.one_hint")}</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input data-testid="scope-future" type="radio" checked={scopeChoice === "future"} onChange={() => setScopeChoice("future")}/>
                                    <div className="text-sm">
                                        <div style={{ color: "var(--text)" }}>{t("schedule.scope.future")}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("schedule.scope.future_hint")}</div>
                                    </div>
                                </label>
                            </div>
                        )}
                        <button data-testid="edit-save" disabled={busy} onClick={submitEdit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{busy ? <Loader2 className="animate-spin" size={14}/> : null} {t("common.save")}</button>
                        <button data-testid="edit-cancel" onClick={() => setEditing(null)} className="w-full text-xs" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                    </div>
                )}
            </div>
        </div>
    );
}
